/**
 * Find the last defined element satisfying the predicate, or return -1.
 * The optional thisArg is bound when invoking the predicate.
 */
export function findLastIndex<T>(
  array: T[],
  predicate: (a: T) => boolean,
  thisArg: any = null,
): number {
  for (let i = array.length - 1; i >= 0; --i) {
    const x = array[i];
    if (x !== undefined && predicate.call(thisArg, x)) {
      return i;
    }
  }

  // Not found.
  return -1;
}

/**
 * Return the zero-based indices of true entries in ascending order.
 */
export function booleansToOrdinals(booleans: boolean[]): number[] {
  return booleans.reduce((arr: number[], value: boolean, ordinal: number) => {
    if (value) {
      arr.push(ordinal);
    }
    return arr;
  }, []);
}

/**
 * Create a fixed-length selection mask; ordinals outside its bounds have no effect.
 */
export function ordinalsToBooleans(
  ordinals: number[],
  length: number,
): boolean[] {
  return Array(length)
    .fill(undefined)
    .map((_, i) => ordinals.includes(i));
}

/**
 * Return contiguous matching runs as half-open index intervals [start, end).
 */
export function findRanges<T>(
  array: T[],
  predicate: (val: T) => boolean,
): [number, number][] {
  let start: number | null = null;
  const result = array.reduce(
    (ranges: [number, number][], value: T, index: number) => {
      if (predicate(value)) {
        if (start === null) {
          // We just started a range of matching values.
          start = index;
        }
      } else {
        if (start !== null) {
          // We just finished a range of matching values.
          ranges.push([start, index]);
          start = null;
        }
      }
      return ranges;
    },
    [],
  );

  if (start !== null) {
    // We ended on a matching range.
    result.push([start, array.length]);
  }

  return result;
}

/**
 * Wrap an inclusive word interval with a prefix and suffix, returning a new array.
 * The offset translates ordinals into array indices; partially overlapping bounds are clamped
 * and intervals entirely outside the array leave it unchanged.
 */
export function wrapWords(
  words: string[],
  firstWordOrdinal: number,
  lastWordOrdinal: number,
  prefix: string,
  suffix: string,
  offset: number,
): string[] {
  words = [...words];
  firstWordOrdinal = firstWordOrdinal + offset;
  lastWordOrdinal = lastWordOrdinal + offset;

  if (firstWordOrdinal < 0) {
    if (lastWordOrdinal < 0) {
      // Out of bounds. Nothing to wrap.
      return words;
    }

    // Beginning is out of bounds. Start at 0.
    firstWordOrdinal = 0;
  }

  if (lastWordOrdinal >= words.length) {
    if (firstWordOrdinal >= words.length) {
      // Out of bounds. Nothing to wrap.
      return words;
    }

    // End is out of bounds. End at the end of the array.
    lastWordOrdinal = words.length - 1;
  }

  words[firstWordOrdinal] = `${prefix}${words[firstWordOrdinal]}`;
  words[lastWordOrdinal] = `${words[lastWordOrdinal]}${suffix}`;

  return words;
}
